import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchSlots,
  createBooking,
  ApiError,
  type BusinessInfo,
  type ServiceInfo,
  type Slot,
} from "../lib/api.ts";
import { BusinessHeader } from "../components/BusinessHeader.tsx";
import { ServiceList } from "../components/ServiceList.tsx";
import { StaffPicker } from "../components/StaffPicker.tsx";
import { DatePicker } from "../components/DatePicker.tsx";
import { SlotGrid } from "../components/SlotGrid.tsx";
import { CustomerForm, type CustomerFormData } from "../components/CustomerForm.tsx";
import { ConfirmSummary } from "../components/ConfirmSummary.tsx";

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  slug: string;
  business: BusinessInfo;
  services: ServiceInfo[];
  staff: Array<{ id: string; name: string; avatar_url?: string | null }>;
}

type Step = "browse" | "cart" | "schedule_details" | "confirm";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatPrice(cents: number | null, currency: string): string {
  if (cents === null || cents === 0) return "Free";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

// ─── Component ───────────────────────────────────────────────────────────────

export function CatalogFlow({ slug, business, services, staff }: Props) {
  const navigate = useNavigate();

  const today = new Date().toISOString().split("T")[0] ?? "";
  const [step, setStep] = useState<Step>("browse");
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);
  const [date, setDate] = useState<string>(today);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [customerData, setCustomerData] = useState<CustomerFormData | null>(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);

  // Fetch slots when on schedule_details step and selections change
  useEffect(() => {
    if (step !== "schedule_details" || selectedServiceIds.length === 0) return;
    setSlotsLoading(true);
    setSlotsError(null);
    setSelectedSlot(null);
    fetchSlots(slug, date, selectedServiceIds, selectedStaffId ?? undefined)
      .then(setSlots)
      .catch(() => setSlotsError("Failed to load available slots."))
      .finally(() => setSlotsLoading(false));
  }, [step, date, selectedServiceIds, selectedStaffId, slug]);

  function toggleService(id: string) {
    setSelectedServiceIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  }

  function handleCustomerSubmit(data: CustomerFormData) {
    setCustomerData(data);
    setStep("confirm");
  }

  async function handleConfirm() {
    if (!selectedSlot || !customerData) return;
    setBookingLoading(true);
    setBookingError(null);
    try {
      const confirmation = await createBooking(slug, {
        service_ids: selectedServiceIds,
        staff_id: selectedStaffId ?? undefined,
        date: date,
        start_time: selectedSlot.start,
        meeting_mode: primaryMode,
        customer: {
          name: customerData.name,
          email: customerData.email,
          phone: customerData.phone,
          notes: customerData.notes || undefined,
        },
      });
      if (confirmation.checkout_url) {
        window.location.href = confirmation.checkout_url;
        return;
      }

      const serviceNames = selectedServices.map((s) => s.name).join(", ");
      const params = new URLSearchParams({
        token: confirmation.cancel_token,
        id: confirmation.appointment_id,
        date: date,
        start_time: selectedSlot.start,
        service: serviceNames,
        staff: selectedStaffMember?.name ?? "",
        meeting_mode: primaryMode,
      });
      navigate(`/${slug}/success?${params.toString()}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setBookingError("That time was just taken. Please pick another slot.");
        setStep("schedule_details");
        setSelectedSlot(null);
      } else {
        setBookingError("Booking failed. Please try again.");
      }
    } finally {
      setBookingLoading(false);
    }
  }

  // Derived data
  const selectedServices = services.filter((s) => selectedServiceIds.includes(s.id));
  const selectedStaffMember = staff.find((m) => m.id === selectedStaffId) ?? null;
  const totalDuration = selectedServices.reduce((sum, s) => sum + s.duration_min, 0);
  const totalPrice = selectedServices.reduce((sum, s) => sum + (s.price_cents ?? 0), 0);
  const primaryCurrency = selectedServices[0]?.currency ?? "USD";
  const primaryMode = selectedServices[0]?.meeting_mode ?? "in_person";
  const hasPrice = selectedServices.some((s) => s.price_cents !== null && s.price_cents > 0);
  const meetingModes = [...new Set(services.map((s) => s.meeting_mode))];

  // Adapt staff to StaffInfo shape expected by StaffPicker
  const staffInfoList = staff.map((m) => ({
    id: m.id,
    name: m.name,
    avatar_url: m.avatar_url ?? null,
    is_active: true,
  }));

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "var(--font)" }}>
      <BusinessHeader business={business} meetingModes={meetingModes} />

      <div className="max-w-lg mx-auto px-4 py-6 space-y-6">

        {/* ── Step 1: Browse (Build your package) ── */}
        {step === "browse" && (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Build your package</h2>
              <p className="text-sm text-gray-500 mt-1">
                Select all the services you'd like to include.
              </p>
            </div>

            <ServiceList
              services={services}
              selected={selectedServiceIds}
              onToggle={toggleService}
            />

            <button
              type="button"
              disabled={selectedServiceIds.length === 0}
              onClick={() => setStep("cart")}
              className="w-full py-3 px-4 rounded-[var(--radius)] text-white text-sm font-semibold disabled:opacity-40 transition-opacity hover:opacity-90"
              style={{ background: "var(--accent)", borderRadius: "var(--radius)" }}
            >
              Review cart &rarr;
            </button>
          </div>
        )}

        {/* ── Step 2: Cart review ── */}
        {step === "cart" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Your cart</h2>
              <button
                type="button"
                onClick={() => setStep("browse")}
                className="text-sm underline"
                style={{ color: "var(--accent)" }}
              >
                Change selection
              </button>
            </div>

            {/* Service cards */}
            <div className="space-y-2">
              {selectedServices.map((service) => (
                <div
                  key={service.id}
                  className="bg-white border border-gray-200 rounded-[var(--radius)] p-4 flex items-center justify-between gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{service.name}</p>
                    <p className="text-sm text-gray-500 mt-0.5">{service.duration_min} min</p>
                  </div>
                  <span className="shrink-0 text-sm font-bold text-gray-900">
                    {formatPrice(service.price_cents, service.currency)}
                  </span>
                </div>
              ))}
            </div>

            {/* Cart total */}
            <div
              className="rounded-[var(--radius)] px-4 py-3 text-white text-sm flex items-center justify-between"
              style={{ backgroundColor: "var(--accent)" }}
            >
              <span>
                {selectedServices.length} service{selectedServices.length !== 1 ? "s" : ""} &middot; {totalDuration} min
              </span>
              {hasPrice && (
                <span className="font-semibold">{formatPrice(totalPrice, primaryCurrency)}</span>
              )}
            </div>

            <button
              type="button"
              onClick={() => setStep("schedule_details")}
              className="w-full py-3 px-4 rounded-[var(--radius)] text-white text-sm font-semibold transition-opacity hover:opacity-90"
              style={{ background: "var(--accent)", borderRadius: "var(--radius)" }}
            >
              Continue &rarr;
            </button>
          </div>
        )}

        {/* ── Step 3: Schedule + Details ── */}
        {step === "schedule_details" && (
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setStep("cart")}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                &larr; Back
              </button>
              <h2 className="text-lg font-semibold text-gray-900">Choose a date &amp; time</h2>
            </div>

            {/* Optional staff picker */}
            {staff.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Provider (optional)</p>
                <StaffPicker
                  staff={staffInfoList}
                  selected={selectedStaffId}
                  onSelect={setSelectedStaffId}
                />
              </div>
            )}

            <DatePicker
              selected={date}
              onSelect={(d) => { setDate(d); setSelectedSlot(null); }}
            />

            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Available times</p>
              <SlotGrid
                slots={slots}
                selected={selectedSlot?.start ?? null}
                onSelect={setSelectedSlot}
                loading={slotsLoading}
                error={slotsError}
              />
            </div>

            {selectedSlot && (
              <div className="pt-2">
                <p className="text-sm font-medium text-gray-700 mb-3">Your details</p>
                <CustomerForm
                  onSubmit={handleCustomerSubmit}
                  onBack={() => setSelectedSlot(null)}
                />
              </div>
            )}
          </div>
        )}

        {/* ── Step 4: Confirm ── */}
        {step === "confirm" && selectedSlot && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Review your booking</h2>
            <ConfirmSummary
              serviceNames={selectedServices.map((s) => s.name)}
              staffName={selectedStaffMember?.name ?? null}
              date={date}
              time={selectedSlot.start}
              durationMin={totalDuration}
              priceCents={hasPrice ? totalPrice : null}
              currency={primaryCurrency}
              meetingMode={primaryMode}
              onConfirm={handleConfirm}
              onBack={() => setStep("schedule_details")}
              loading={bookingLoading}
              error={bookingError}
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="text-center py-6 text-xs text-gray-400">
        Powered by{" "}
        <a
          href="https://zedule.app"
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium hover:text-gray-600 transition-colors"
        >
          Zedule
        </a>
      </div>
    </div>
  );
}
